"use client";

import Image from "next/image";
import { memo } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { setFilters } from "@/feature/flight/search/state/slices/extract-flight-filter.slice";
import { selectFilterOptions } from "@/feature/flight/search/state/selectors";
import { priceFormatter } from "@/lib/price-formatter";
import { absoluteUrl } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import AccordionSection from "./accordion-section";

const AirlineFilter = memo(() => {
  const dispatch = useAppDispatch();
  const isResponseEnd = useAppSelector(
    (state) => state.flightSearchStatus.isResponseEnd,
  );
  const { selectOption, defaultOption } = useAppSelector(
    selectFilterOptions,
  );

  const selected = selectOption?.airline ?? [];
  const airlines = defaultOption?.airlines_lowest_price ?? [];

  const handleChange = (value?: string) => {
    if (!value) return;

    const isSelected = selected.includes(value);
    const updatedAirlines = isSelected
      ? selected.filter((airline) => airline !== value)
      : [...selected, value];

    dispatch(setFilters({ airline: updatedAirlines }));
  };

  return (
    <AccordionSection value="airlines" label="Airlines">
      {airlines.map((airline, _i) => (
        <div key={_i} className="flex items-center space-x-2">
          <Checkbox
            disabled={!isResponseEnd}
            id={airline.airline_name}
            checked={selected.includes(airline.airline_name)}
            onCheckedChange={() => handleChange(airline.airline_name)}
          />
          <Label
            htmlFor={airline.airline_name}
            className="font-normal cursor-pointer w-full flex justify-between"
          >
            <div className="flex items-center space-x-2">
              <div className="inline-block mr-2">
                {airline.airline_logo ? (
                  <Image
                    alt={`${airline.airline_name} logo`}
                    src={absoluteUrl(airline.airline_logo, true)}
                    width={15}
                    height={15}
                    className="rounded"
                    loading="lazy"
                  />
                ) : (
                  <div className="size-[15px] rounded bg-muted flex items-center justify-center text-[9px] font-semibold uppercase">
                    {airline.airline_name?.slice(0, 1) ?? "A"}
                  </div>
                )}
              </div>
              <span className="line-clamp-1">{airline.airline_name}</span>
            </div>

            <div className="">{priceFormatter(airline.price)}</div>
          </Label>
        </div>
      ))}
    </AccordionSection>
  );
});

AirlineFilter.displayName = "AirlineFilter";
export default AirlineFilter;
